import * as Excel from 'exceljs/dist/exceljs';
import { saveAs } from 'file-saver';
import { searchDdirTrend, wpidSearchDdirTrend } from './apiService';

async function downloadHistoricInventoryData(selectedItem, dcNbr) {
  if (!selectedItem) return;
  const { itemNbr, legacyItemNbr } = selectedItem;

  try {
    const timestampStr = (new Date()).toLocaleString();
    const historicInventoryTableData = selectedItem.wpidNbr
      ? await wpidSearchDdirTrend(
          [itemNbr],
          legacyItemNbr != null ? [legacyItemNbr] : [],
          dcNbr
        )
      : await searchDdirTrend(itemNbr, legacyItemNbr, dcNbr);
    const workbook = await configWorkbookForHistoricData(historicInventoryTableData, selectedItem, dcNbr);
    await writeFileAndDownload(workbook, `DDIR-Historic-${timestampStr}.xlsx`);
  } catch (error) {
    console.error('Historic Download failed:', error);
  }
}

async function configWorkbookForHistoricData(historicInventoryTableData, item, dcNbr) {
  const workbook = new Excel.Workbook();
  const ws = workbook.addWorksheet('Historic');

  const metaHeaders = ['Item Number', 'Item Description', 'Category', 'Vendor Number', 'Vendor Name', 'DC'];
  const combinedDesc = `${item.itemDesc || ''} | ${item.item2Desc || ''}`;
  const metaValues = [
    item.itemNbr ?? '',
    combinedDesc,
    item.category ?? '',
    item.vendorNbr ?? '',
    item.vendorName ?? '',
    dcNbr ?? ''
  ];
  ws.addRow(metaHeaders);
  const valuesRow = ws.addRow(metaValues);

  ws.getRow(1).eachCell(cell => {
    cell.font = { bold: true };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } };
    cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    cell.alignment = { horizontal: 'center' };
  });
  valuesRow.eachCell(cell => {
    cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    cell.alignment = { horizontal: 'center' };
  });

  ws.addRow(['']);

  const metricsDisplay = ['Demand', 'Ordered', 'Received', 'Inventory', 'Sales'];
  const metricKeys = ['demand', 'ordered', 'received', 'inventory', 'sales'];
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const extractMonthDay = str => {
    if (!str || typeof str !== 'string') return { m: '', d: '' };
    let m = '', d = '';
    if (str.includes('-')) {
      const p = str.split('-');
      if (p.length >= 3) { m = p[1]; d = p[2]; }
    } else if (str.includes('/')) {
      const p = str.split('/');
      if (p.length >= 2) { m = p[0]; d = p[1]; }
    } else {
      const digits = str.replace(/\D/g, '');
      if (digits.length >= 4) { m = digits.slice(0, 2); d = digits.slice(2, 4); }
    }
    m = m.padStart(2, '0'); d = d.padStart(2, '0');
    return { m, d };
  };

  const normalizeDate = str => {
    const { m, d } = extractMonthDay(str);
    return (m && d) ? `${m}/${d}` : str;
  };

  const toMonthDayLong = str => {
    const { m, d } = extractMonthDay(str);
    const mNum = parseInt(m, 10);
    if (!m || !d || isNaN(mNum) || mNum < 1 || mNum > 12) return str;
    return `${monthNames[mNum - 1]} ${d}`;
  };

  const getSum = arr => Array.isArray(arr)
    ? arr.filter(v => v !== null && v !== undefined && v !== '')
      .reduce((a, b) => a + (Number(b) || 0), 0)
    : 0;

  const getAvg = arr => {
    if (!Array.isArray(arr)) return 0;
    const cleaned = arr.filter(v => v !== null && v !== undefined && v !== '');
    if (!cleaned.length) return 0;
    const total = cleaned.reduce((a, b) => a + (Number(b) || 0), 0);
    return Math.round(total / cleaned.length);
  };

  historicInventoryTableData.forEach(period => {
    const datesRaw = Array.isArray(period.date) ? period.date : [];
    const formattedDates = datesRaw.map(normalizeDate);
    const weekLabel = `Week ${period.week}, ${period.year}`;

    const bannerRow = ws.addRow([weekLabel]);
    const totalCols = 2 + formattedDates.length + 1;
    ws.mergeCells(bannerRow.number, 1, bannerRow.number, totalCols);
    const bannerCell = ws.getRow(bannerRow.number).getCell(1);
    bannerCell.font = { bold: true, size: 14 };
    bannerCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6F3FF' } };
    bannerCell.alignment = { horizontal: 'left', vertical: 'middle' };
    bannerCell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };

    const headerVals = ['Period', 'Area', ...formattedDates, 'Total'];
    const headerRow = ws.addRow(headerVals);
    headerRow.eachCell(cell => {
      const isBold =
        cell.value === 'Period' ||
        cell.value === 'Area' ||
        cell.value === 'Total' ||
        formattedDates.includes(cell.value);
      cell.font = { bold: isBold };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FB' } };
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      cell.alignment = { horizontal: 'center' };
    });

    const periodDisplay = datesRaw.length
      ? `${toMonthDayLong(datesRaw[0])} - ${toMonthDayLong(datesRaw[datesRaw.length - 1])}`
      : '';

    const startMetricRow = ws.lastRow.number + 1;

    metricKeys.forEach((key, idx) => {
      const data = period.data && period.data[key] ? period.data[key] : [];
      const padded = [...data];
      while (padded.length < formattedDates.length) padded.push('');
      const total = key === 'inventory' ? getAvg(data) : getSum(data);

      const excelRow = ws.addRow([
        idx === 0 ? periodDisplay : '',
        metricsDisplay[idx],
        ...padded.map(v => (v === null ? '' : v)),
        total
      ]);

      excelRow.eachCell((cell, col) => {
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        if (col === 1 && idx === 0) {
          cell.font = { bold: true };
          cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        } else if (col === 2) {
          cell.font = { bold: false };
          cell.alignment = { horizontal: 'left' };
        } else if (col >= 3 && col < 3 + formattedDates.length) {
          cell.alignment = { horizontal: 'right' };
        } else {
          cell.alignment = { horizontal: 'center' };
        }
      });
    });

    const endMetricRow = ws.lastRow.number;
    ws.mergeCells(startMetricRow, 1, endMetricRow, 1);
    const mergedCell = ws.getRow(startMetricRow).getCell(1);
    mergedCell.font = { bold: true };
    mergedCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    mergedCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F8FF' } };

    ws.addRow(['']);
  });

  ws.getColumn(1).width = 18;
  ws.getColumn(2).width = 40;
  for (let c = 3; c <= ws.columnCount; c++) ws.getColumn(c).width = 15;

  return workbook;
}

async function writeFileAndDownload(workbook, filename) {
  const buf = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buf]), filename);
}

export { downloadHistoricInventoryData };
